import React, { useState, useRef } from 'react';
import CloseIcon from '../../../../../../assets/svg/icons/common/CloseIcon';
import CheckIcon from '../../../../../../assets/svg/icons/common/CheckIcon';
import CopyIcon from '../../../../../../assets/svg/icons/common/CopyIcon';
import styles from './style.module.scss';

/**
 * SendToMerchantModal Component
 * Renders a modal that displays a merchant link and provides options to copy the link,
 * continue in person, or view applications
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {string} props.merchantLink - The link to be sent to the merchant
 * @param {Function} props.onContinue - Callback function when continue in person is clicked
 * @param {Function} props.onViewApplications - Callback function when view applications is clicked
 */
const SendToMerchantModal = ({ isOpen, onClose, merchantLink, onContinue, onViewApplications }) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const linkInputRef = useRef(null);

  /**
   * Handles copying the merchant link to clipboard
   * Supports both modern and legacy clipboard APIs
   * Shows a temporary success message when copied
   */
  const handleCopyLink = async () => {
    try {
      if (linkInputRef.current) {
        linkInputRef.current.select();
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(merchantLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = merchantLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }

      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div />
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        
        <div className={styles.body}>
          <div className={styles.head}>
            <div className={styles.successIcon}>
              <div className={styles.successIconInner}>
                <div className={styles.successIconCore}>
                  <CheckIcon />
                </div>
              </div>
            </div>
            <h2 className={styles.headTitle}>Link sent to Merchant</h2>
          </div>
          
          <p className={styles.description}>
            Application(s) have been sent to the merchant for signature.
          </p>
          
          <div className={styles.linkContainer}>
            <input
              ref={linkInputRef}
              type="text"
              value={merchantLink}
              readOnly
              className={styles.linkInput}
              onClick={(e) => e.target.select()}
            />
            <button
              className={styles.copyButton}
              onClick={handleCopyLink}
              title="Copy to clipboard"
            >
              {linkCopied ? <CheckIcon /> : <CopyIcon />}
              <span>{linkCopied ? 'Copied!' : 'Copy link'}</span>
            </button>
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.buttonGroup}>
            <button
              className={styles.continueButton}
              onClick={onContinue}
            >
              Continue in Person
            </button>
            <button
              className={styles.applicationsButton}
              onClick={onViewApplications}
            >
              Go to Applications
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendToMerchantModal; 